import { useMutation, useQuery } from "@tanstack/react-query";
import { useId, useState } from "react";
import type { ApiClient } from "../api/client";
import { ApiProblem } from "../api/problem";
import { endpoints } from "../api/endpoints";
import type { LocalAuthStatus } from "../api/types";
import { localSession, readStoredCredentials, type LocalSession } from "../auth/localAuth";
import { formatDateTime, formatTimeOfDay } from "../format/datetime";
import { Notice, ProblemAlert } from "./ProblemAlert";
import { Button, Dot } from "@lcc/ui";

/** `GET /local-auth/status` (public), polled so the console offers the local sign-in only when it works. */
export function useLocalAuthStatus(api: ApiClient, refreshMs: number) {
  return useQuery({
    queryKey: ["fer-public", "local-auth-status"],
    queryFn: () => endpoints.getLocalAuthStatus(api),
    refetchInterval: refreshMs ? Math.max(refreshMs, 15_000) : false,
    retry: false,
    staleTime: 10_000,
  });
}

function probeWords(s: LocalAuthStatus): string {
  const down = [
    !s.probes.internal.ok && `the identity service (failing since ${formatDateTime(s.probes.internal.failing_since)})`,
    !s.probes.public.ok && `the public sign-in route (failing since ${formatDateTime(s.probes.public.failing_since)})`,
  ].filter(Boolean);
  return down.length ? `Unreachable: ${down.join(" and ")}.` : "Both sign-in routes answer.";
}

/**
 * Sign in to FER alone while the identity service is unreachable (either probe failing for 60 s): choose the
 * credential enrolled on this console and type its PIN; the device secret comes from this browser's storage. Offered
 * only while `/local-auth/status` says local sign-in is available.
 */
export function LocalSignIn({ api, apiBaseUrl, status, onSession }: { api: ApiClient; apiBaseUrl: string; status: LocalAuthStatus | undefined; onSession: (s: LocalSession) => void }) {
  const stored = readStoredCredentials(apiBaseUrl);
  const [credentialId, setCredentialId] = useState(stored[0]?.credential_id ?? "");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const baseId = useId();
  const m = useMutation({
    mutationFn: (v: { credential_id: string; device_secret: string; pin: string }) => endpoints.localSignIn(api, v),
  });

  if (!status) return null;
  if (!status.local_sign_in_available) {
    return (
      <Notice>
        The FER local sign-in is offered only while the identity service is unreachable. {probeWords(status)} Sign in normally.
      </Notice>
    );
  }
  if (stored.length === 0) {
    return (
      <Notice tone="warning">
        The identity service is unreachable ({probeWords(status)}), but no FER local credential is enrolled on this console.
        Staff enrol a console while signed in normally with their second factor (Local sign-in screen).
      </Notice>
    );
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const c = stored.find((x) => x.credential_id === credentialId);
    if (!c) return setErr("Choose whose credential to use.");
    if (!/^[0-9]{6,12}$/.test(pin)) return setErr("The PIN is 6 to 12 digits.");
    setErr(null);
    m.mutate(
      { credential_id: c.credential_id, device_secret: c.device_secret, pin },
      {
        onSuccess: (t) => {
          setPin("");
          const s = localSession(t, c.credential_id);
          if (!s) return setErr("The local sign-in answer could not be read. Try again.");
          onSession(s);
        },
        onError: () => setPin(""),
      },
    );
  };

  return (
    <form className="fer-panel fer-form" onSubmit={submit} noValidate aria-label="FER local sign-in">
      <h3>Sign in to FER alone</h3>
      <p>
        {probeWords(status)} You can sign in to FER alone with your PIN. Only intake, dispatch, the maps and reading
        stations, appliances and hydrants work; everything you do is marked as a local sign-in.
      </p>
      <div className="fer-field">
        <label htmlFor={`${baseId}-who`}>
          <span>Who is signing in (required)</span>
        </label>
        <select id={`${baseId}-who`} value={credentialId} onChange={(e) => setCredentialId(e.target.value)}>
          {stored.map((c) => (
            <option key={c.credential_id} value={c.credential_id}>
              {c.display_name ?? "Staff"} – {c.device_label}
            </option>
          ))}
        </select>
      </div>
      <p className="fer-hint">
        Credential id: <code>{credentialId}</code> (a FER admin unlocks a locked credential by this id).
      </p>
      <div className="fer-field">
        <label htmlFor={`${baseId}-pin`}>
          <span>PIN (required)</span>
        </label>
        <input id={`${baseId}-pin`} type="password" inputMode="numeric" autoComplete="off" value={pin} maxLength={12} onChange={(e) => setPin(e.target.value)} />
      </div>
      {err && (
        <p className="fer-form-error" role="alert">
          {err}
        </p>
      )}
      <ProblemAlert error={m.error} />
      {m.error instanceof ApiProblem && m.error.code === "credential_locked" && (
        <Notice tone="warning">
          This credential is locked. Ask a FER admin to unlock credential id <strong><code>{credentialId}</code></strong>.
        </Notice>
      )}
      <div className="fer-actions">
        <Button type="submit" disabled={m.isPending}>
          Sign in with PIN
        </Button>
      </div>
    </form>
  );
}

/** The banner of a FER local session: until when, what works, and whether normal sign-in is back. */
export function LocalSessionBanner({ session, status, onEnd }: { session: LocalSession; status: LocalAuthStatus | undefined; onEnd: () => void }) {
  const back = status && !status.local_sign_in_available;
  return (
    <div className="lcc-notice fer-notice fer-banner" role="status" aria-label="Local sign-in">
      <Dot tone="warning" />
      <div className="lcc-notice__body">
        <p>
          <strong>Signed in to FER alone</strong> ({session.principal.displayName ?? "local sign-in"}) until {formatTimeOfDay(session.expiresAt)}. Only
          intake, dispatch, the maps and reading stations, appliances and hydrants work; your actions are marked as a local
          sign-in.
        </p>
        {back && <p>Normal sign-in works again. Your local session continues until it ends; sign in normally when convenient.</p>}
        <div className="fer-actions fer-actions--start">
          <Button size="sm" onClick={onEnd}>
            End the local session
          </Button>
        </div>
      </div>
    </div>
  );
}
